import {styleRule, StyleSheet} from "../../../../../../stem-core/ui/Style";
import {registerStyle} from "../../../../../../stem-core/ui/style/Theme";
import {UI} from "../../../../../../stem-core/ui/UIBase";
import type {StoreId} from "../../../../../../stem-core/state/State";
import type {SubscriptionCoverage} from "../../../../../../client/state/SubscriptionCoverageStore";
import {SubscriptionOffer} from "../../../../../../client/state/SubscriptionOfferStore.js";
import {iFrameMerchantService} from "../../../../../services/IframeMerchantService.js";
import {TNR_ALIAS} from "../../../../../../blink-sdk/Constants.js";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../../../../../../blink-sdk/utils/AnalyticsClient.js";
import {TNRSubscriptionCoverageCard} from "./TNRSubscriptionCoverageCard.jsx";
import {SubscriptionCoverageCard} from "./SubscriptionCoverageCard.jsx";
import {Flow} from "../../../../flows/Flow.js";
import {haveSameElements, isSmallScreen} from "../../../../../UtilsLib.js";
import {iFrameState} from "../../../../../services/IFrameState.js";
import {DescriptionItemType} from "./CoverageDescription.jsx";
import {SubscriptionBenefit} from "../../../../../../client/state/SubscriptionBenefitStore.js";

// One card: {coverage, offers}. The coverage is a real SubscriptionCoverage, except for the offers with
// extra benefits, where addExtraBenefit builds a stand-in with just getName()/getDescription().
type CoverageEntry = Record<string, any>;

function addExtraBenefit(coverage: SubscriptionCoverage, offer: SubscriptionOffer, extraEntries: CoverageEntry[]) {
    for (const extraEntry of extraEntries) {
        if (haveSameElements(extraEntry.offers[0].extraBenefitIds, offer.extraBenefitIds)) {
            extraEntry.offers.push(offer);
            return;
        }
    }

    extraEntries.push({
        coverage: {
            getName() {
                return offer.getName()
            },
            getDescription() {
                const benefits = offer.extraBenefitIds.map((benefitId: StoreId) => SubscriptionBenefit.get(benefitId));
                return {
                    items: [
                        {type: DescriptionItemType.COVERAGE, coverageId: coverage.id},
                        {type: DescriptionItemType.PLUS},
                        ...benefits.map((benefit: SubscriptionBenefit) => ({type: DescriptionItemType.PERK, text: benefit.description})),
                    ]
                }
            }
        },
        offers: [offer],
    });
}


function BuildCoverageEntries(coverage: SubscriptionCoverage) {
    const offers: SubscriptionOffer[] = [];
    const entries: CoverageEntry[] = [];

    for (const offer of coverage.getAvailableOffers()) {
        // TODO If there are multiple plans with the same extra benefit, these should be grouped together
        if (offer.extraBenefitIds.length > 0) {
            addExtraBenefit(coverage, offer, entries);
            continue;
        }
        offers.push(offer);
    }

    if (offers.length > 0) {
        entries.unshift({
            coverage,
            offers,
        });
    } else {
        // TODO: handle this case, don't care for now
        console.warn("This is not configured correctly");
    }

    return entries;
}

export class SubscriptionCardsListStyle extends StyleSheet {
    @styleRule
    container = {
        maxWidth: 1600,
        display: "inline-flex",
        flexWrap: "wrap",
        justifyContent: "center",
        width: "fit-content"
    }

    @styleRule
    subscriptionCard = {
        margin: isSmallScreen() ? "8px 0px" : 16,
    }
}


@registerStyle(SubscriptionCardsListStyle)
export class SubscriptionCardsList extends UI.Element {
    analyticsDispatched = false;

    async handleSubscribeClick(offer: SubscriptionOffer) {
        dispatchAnalyticsEvent(AnalyticsEventType.SUBSCRIPTION_SELECTED, {offerId: offer.id});

        await Flow.auth.promptIfNeeded();

        await Flow.subscriptionPlan.selectValue(offer);
    }

    render() {
        const {styleSheet} = this;

        // TODO: Have a special ordering field instead of "id".
        const subscriptionCoverages = SubscriptionOffer.all()
            .map(offer => offer.getCoverage())
            .filter((coverage, index, arr) => arr.indexOf(coverage) === index) // ensure unique coverages are returned
            .sort((coverage1, coverage2) => (coverage1.id as number) - (coverage2.id as number));
        const cardEntries = [];

        // TODO @cleanup so looks like there should be an option in the panel to be per offer or grouped per coverage
        if (iFrameMerchantService.isMerchant(TNR_ALIAS)) {
            for (const offer of SubscriptionOffer.all()) {
                cardEntries.push({
                    coverage: offer.getCoverage(),
                    offers: [offer]
                });
            }
        } else {
            for (const coverage of subscriptionCoverages) {
                cardEntries.push(...BuildCoverageEntries(coverage));
            }
        }

        if (!this.analyticsDispatched) {
            dispatchAnalyticsEvent(AnalyticsEventType.SHOW_SUBSCRIPTION_COVERAGE_CARDS, {
                coverageCards: cardEntries.map(({coverage, offers}) => ({
                    coverageId: coverage.id,
                    offerIds: offers.map((offer: SubscriptionOffer) => offer.id),
                }))
            });
            this.analyticsDispatched = true;
        }

        const CoverageCardClass = iFrameMerchantService.isMerchant(TNR_ALIAS) ? TNRSubscriptionCoverageCard : SubscriptionCoverageCard;

        return cardEntries.map(({offers, coverage}, index) => {
            // TODO @cleanup move this and the offer inside the TNRSubscriptionCoverageCard logic
            const offer = offers[0];
            const subscriptionExtraConfig = iFrameState.iframeParams?.subscriptionOfferConfigs?.[offer.id];
            const bestValue = subscriptionExtraConfig?.bestValue ?? index === SubscriptionOffer.all().length - 1;
            const customHeaderType = subscriptionExtraConfig?.customHeaderType ?? (bestValue ? "bestValue" : null)

            return <CoverageCardClass
                className={styleSheet.subscriptionCard}
                offers={offers}
                offer={offers[0]}
                coverage={coverage}
                customHeaderType={customHeaderType}
                onSubscriptionOfferSelected={(offer) => this.handleSubscribeClick(offer)}
            />
        });
    }
}
